import { ChangeDetectionStrategy, Component, Input } from '@angular/core';

/**
 * What an applicant sees the moment they submit.
 *
 * One component because it was one screen written seven times: every form page had
 * its own copy of the same alert, so fixing the confirmation on Tourist Pass left the
 * other six saying "Apply for a Recreation Permit" over an issued permit, with two
 * full-width stacked buttons underneath.
 *
 * The permit number is deliberately absent (TLC, 18 Sep): it reaches the applicant only
 * on the issued PDF, never on-screen, so nobody can screenshot a number in place of the
 * permit. The status leads as a quiet line, with the download/view actions below it.
 */
@Component({
    selector: 'app-permit-result',
    templateUrl: './permit-result.component.html',
    styleUrls: ['./permit-result.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class PermitResultComponent {
    /** `issued` when the permit exists now; anything else is awaiting assessment. */
    @Input() state: 'issued' | 'pending' = 'pending';

    // No permitNumber input by design (TLC, 18 Sep): the number is never shown on the
    // confirmation screen, only on the issued PDF the applicant downloads.

    /** Where the confirmation went, so they can see it before closing the tab. */
    @Input() email?: string | null;

    /** Shown only when the permit covers more than the applicant. */
    @Input() partySize?: number;

    /** Overrides the default explanation. Buttons are projected as content. */
    @Input() body?: string;

    public get issued(): boolean {
        return 'issued' === this.state;
    }

    public get heading(): string {
        return this.issued ? 'Permit issued' : 'Application received';
    }

    public get defaultBody(): string {
        return this.issued
            ? ''
            : 'Tiwi Land Council need to review this before it can be approved. We will be in touch once it has been assessed - you do not need to do anything else.';
    }
}
